/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect, useRef, useId} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {useLocation} from 'react-router-dom'

// Chakra Components
import {
    Box,
    Flex,
    // Hooks
    useMultiStyleConfig,
    Stack,
    omitThemingProps,
    IconButton,
    List,
    Img,
    ListItem,
    Spacer,
    Button
} from '@chakra-ui/react'
import {EnterKeyNumber} from '../../constants'
import lazyLoadable from '../../utils/lazy-loadable'
import {getFileNameFromUrl} from '../../utils/url'
import DynamicImage from '../dynamic-image'
import {HotspotIcon} from '../icons'
import LoadingSpinner from '../loading-spinner'
import LinePagination from './line-pagination'
const ReactPlayerLoader = lazyLoadable(() => import('@brightcove/react-player-loader'), {
    ssr: false,
    fallback: <LoadingSpinner />
})

/**
 * The image gallery displays a hero image and thumbnails below it. You can control which
 * image groups that are use by passing in the current selected variation values.
 */
const ImageGallery = (props) => {
    const id = useId()
    const {formatMessage} = useIntl()
    const styles = useMultiStyleConfig('ImageGallery', props)
    const {
        allowAutoplay,
        ariaLabel,
        children,
        cursor,
        items = [],
        index: initialSelectedIndex = 0,
        imageProps,
        hotspots,
        navLeftIcon,
        navRightIcon,
        preload,
        showChildrenIn,
        showPagination,
        showThumbnails,
        onClick,
        enableZoom,
        onHotspotClick,
        onChange
    } = omitThemingProps(props)
    const brightcovePlayersRefs = useRef([])

    /* Touch event slide/swipe starting point used to calculate left or right direction */
    const touchStart = useRef(0)

    /* Current selected image index */
    const [selectedIndex, _setSelectedIndex] = useState(initialSelectedIndex)
    const setSelectedIndex = (index, notifyChange) => {
        _setSelectedIndex(index)
        if (notifyChange) {
            onChange?.(index)
        }
    }

    /* Indicator that sets to true when slider starts scrolling to prevent user clicks while slider still scrolling to next/prev image */
    const [stillScrolling, setStillScrolling] = useState(false)

    const location = useLocation()
    const scrollRef = useRef()

    useEffect(() => {
        // 1. always make sure we are selecting an index that exists
        // 2. reset position when items change - that is an indirect indication of pdp change
        const index = initialSelectedIndex <= items.length ? initialSelectedIndex : 0
        // reset the selected index when location search changes or initially selected index changes
        _setSelectedIndex(index)
        if (scrollRef.current) {
            scrollRef.current.scrollLeft = scrollRef.current.clientWidth * index
        }
    }, [location.search, initialSelectedIndex, items])

    useEffect(() => {
        for (const playerRef of brightcovePlayersRefs.current) {
            playerRef?.player?.pause?.()
        }
    }, [selectedIndex])

    /* Copied from ImageGallery component with an addition of touch attribute */
    // Scroll the container left or right by 1 image. Passing no args or `1`
    // scrolls to the right, and passing `-1` scrolls left.
    const scroll = (direction = 1, touch) => {
        if (scrollRef.current) {
            /* If scrolling to next/prev image is not finished yet - return */
            if (stillScrolling) return

            /* Calculate slide direction (left/right) from touchevent in mobileview */
            const {touchStart, touchEnd} = touch || {}

            if (touchStart) {
                direction = touchEnd < touchStart ? 1 : -1
            }

            /* In mobileview prevent the user from scrolling just by clicking the image */
            if ((touchStart && Math.abs(touchStart - touchEnd) > 50) || !touchStart) {
                let nextScrollPosition = 0
                const itemWidth = scrollRef.current.clientWidth
                if (direction < 0 && selectedIndex === 0) {
                    /* Clicking prev when the slider is on first image scrolls all the way to last image */
                    nextScrollPosition = itemWidth * items.length + 1
                    setSelectedIndex(items.length - 1, true)
                } else if (direction > 0 && selectedIndex + 1 === items.length) {
                    /* Clicking next when the slider is on last image scrolls all the way to first image */
                    nextScrollPosition = 0
                    setSelectedIndex(0, true)
                } else {
                    /* Scroll by one image left/right */
                    nextScrollPosition = (selectedIndex + direction) * itemWidth
                    setSelectedIndex(selectedIndex + direction, true)
                }
                scrollRef.current.scrollTo({
                    top: 0,
                    left: nextScrollPosition,
                    behavior: 'smooth'
                })

                /* Prevent the user from clicking slider arrows while scrolling animation is not done yet */
                setStillScrolling(true)
                setTimeout(() => {
                    setStillScrolling(false)
                }, 500)
            }
        }
    }

    /* When it is first time playing the video - always open on fullscreen */
    const onSuccess = ({ref}) => {
        const {player_, posterImage, bigPlayButton} = ref

        posterImage.el_.addEventListener('click', () => {
            player_.requestFullscreen()
        })

        bigPlayButton.el_.addEventListener('click', () => {
            player_.requestFullscreen()
        })

        player_.el().style.width = '100%'
        player_.el().style.height = '100%'
        player_.el().parentElement.style.width = '100%'
        player_.el().parentElement.style.height = '100%'

        // #202: Unnecessary ARIA attributes can cause unexpected behavior for screen reader users.
        // remove aria-live from the play button label
        // created in https://github.com/videojs/video.js/blob/v8.20.0/src/js/clickable-component.js#L123
        // related upstream tickets:
        // * https://github.com/videojs/video.js/issues/8912
        // * https://github.com/videojs/video.js/discussions/8854
        player_.bigPlayButton?.controlTextEl_?.removeAttribute('aria-live')
    }

    return (
        <>
            {items?.length > 0 && (
                <Box {...styles.container}>
                    <Box
                        as="section"
                        position="relative"
                        backgroundColor="stokkeGray.light"
                        {...styles.imageAndOverlayContainer}
                        aria-label={
                            ariaLabel ||
                            formatMessage({
                                id: 'product.carousel.image',
                                defaultMessage: 'Image Carousel'
                            })
                        }
                    >
                        <Box position="relative" {...styles.imageContainer}>
                            <Stack
                                id={id}
                                data-testid="image-gallery-scroller"
                                tabIndex="0"
                                ref={scrollRef}
                                direction="row"
                                spacing={0}
                                wrap="nowrap"
                                overflowX="scroll"
                                sx={{
                                    scrollSnapType: 'x mandatory',
                                    WebkitOverflowScrolling: 'touch' // Safari touch scrolling needed for scroll snap
                                }}
                                css={{
                                    ...styles.scrollBar
                                }}
                                onTouchStart={(e) => {
                                    touchStart.current = e.changedTouches[0].clientX
                                }}
                                onTouchEnd={(e) => {
                                    scroll(1, {
                                        touchStart: touchStart.current,
                                        touchEnd: e.changedTouches[0].clientX
                                    })
                                }}
                            >
                                {/* all images available for swipe */}
                                {items.map(({autoplay, position: _, ...item}, idx) => {
                                    const visible = !stillScrolling && idx === selectedIndex
                                    return (
                                        <Box
                                            key={item?.id || idx}
                                            {...styles.imageAspectParent}
                                            data-testid={'slider-child'}
                                            role="group"
                                            aria-label={formatMessage(
                                                {
                                                    id: 'global.carousel.slide_x_of_y',
                                                    defaultMessage: 'Slide {index} of {total}'
                                                },
                                                {
                                                    index: idx + 1,
                                                    total: items.length
                                                }
                                            )}
                                        >
                                            <Flex {...styles.imageAspectChild}>
                                                {/* image background color */}
                                                <Button
                                                    {...styles.imageBackgroundOverlay}
                                                    variant="unstyled"
                                                    maxWidth="100%"
                                                    data-testid="image-background-overlay"
                                                    cursor={cursor}
                                                    onClick={(event) => onClick?.(event, item, idx)}
                                                    aria-label={formatMessage({
                                                        id: 'image_gallery.button.assistive_msg.zoom',
                                                        defaultMessage: 'Open Zoom Gallery'
                                                    })}
                                                    aria-hidden={!visible}
                                                    tabIndex={visible ? null : -1}
                                                />

                                                {item?.accountId ? (
                                                    <Box
                                                        display={
                                                            // hide the video once it's loaded as there is no option to hide it in the brightcove player
                                                            !visible ? 'none' : null
                                                        }
                                                        height="100%"
                                                    >
                                                        <ReactPlayerLoader
                                                            {...item}
                                                            options={{
                                                                // when autoplay is allowed and item is set to autoplay,
                                                                // then autoplay but muted
                                                                autoplay: allowAutoplay && autoplay,
                                                                muted: allowAutoplay && autoplay
                                                            }}
                                                            embedOptions={{
                                                                AspectRatio: '1:1'
                                                            }}
                                                            onSuccess={onSuccess}
                                                            ref={(playerRef) => {
                                                                brightcovePlayersRefs.current[idx] =
                                                                    playerRef
                                                            }}
                                                        />
                                                    </Box>
                                                ) : (
                                                    <>
                                                        <DynamicImage
                                                            data-testid="emotional-gallery-image"
                                                            {...styles.image}
                                                            {...imageProps}
                                                            src={`${
                                                                item.disBaseLink || item.link
                                                            }[?sw={width}&q=60]`}
                                                            imageProps={{
                                                                ...styles.image?.imageProps,
                                                                ...imageProps?.imageProps,
                                                                alt: item.alt,
                                                                loading: idx > 0 ? 'lazy' : 'eager'
                                                            }}
                                                            enableZoom={enableZoom}
                                                            preload={preload && idx === 0}
                                                            aria-hidden={!visible}
                                                        />
                                                        {hotspots?.[
                                                            getFileNameFromUrl(
                                                                item.disBaseLink || item.link
                                                            )
                                                        ]?.map((hotspot, index) => (
                                                            <IconButton
                                                                key={index}
                                                                data-testid="hotspot"
                                                                {...styles.hotspot}
                                                                left={`${hotspot.x}%`}
                                                                top={`${hotspot.y}%`}
                                                                variant="hotspot"
                                                                aria-label={hotspot.label}
                                                                onClick={() =>
                                                                    onHotspotClick(hotspot)
                                                                }
                                                                role="button"
                                                                aria-hidden={!visible}
                                                                tabIndex={visible ? null : -1}
                                                            >
                                                                <HotspotIcon boxSize={10} />
                                                            </IconButton>
                                                        ))}
                                                    </>
                                                )}
                                            </Flex>
                                        </Box>
                                    )
                                })}

                                {/* controls if we have more than one image/video */}
                                {items.length > 1 && (
                                    <>
                                        {navLeftIcon && (
                                            <IconButton
                                                {...styles.navButton}
                                                {...styles.navButtonLeft}
                                                data-testid="emotional-gallery-nav-left"
                                                aria-label="Scroll images left"
                                                onClick={() => scroll(-1)}
                                            >
                                                {React.cloneElement(navLeftIcon, {
                                                    ...styles.sliderButtons
                                                })}
                                            </IconButton>
                                        )}

                                        {navRightIcon && (
                                            <IconButton
                                                {...styles.navButton}
                                                {...styles.navButtonRight}
                                                data-testid="emotional-gallery-nav-right"
                                                aria-label="Scroll images right"
                                                onClick={() => scroll(1)}
                                            >
                                                {React.cloneElement(navRightIcon, {
                                                    ...styles.sliderButtons
                                                })}
                                            </IconButton>
                                        )}
                                    </>
                                )}
                            </Stack>
                        </Box>

                        <Flex {...styles.overlay}>
                            {showPagination && (
                                <LinePagination
                                    current={selectedIndex}
                                    total={items.length}
                                    onClick={scroll}
                                />
                            )}
                            <Spacer></Spacer>

                            {showChildrenIn === 'overlay' && children}
                        </Flex>
                    </Box>

                    {showThumbnails && (
                        <List {...styles.thumbnailsContainer}>
                            {items.map((item, index) => {
                                const selected = index === selectedIndex

                                const isVideo = item.accountId
                                const THUMBNAIL_WIDTHS = {base: 51, xl: 86}

                                return (
                                    <ListItem
                                        {...styles.thumbnailImageItem}
                                        {...(selected && styles.thumbnailImageItemSelected)}
                                        tabIndex={0}
                                        key={index}
                                        data-testid="image-gallery-thumbnails"
                                        onKeyDown={(e) => {
                                            if (e.keyCode === EnterKeyNumber) {
                                                return scroll(index - selectedIndex)
                                            }
                                        }}
                                        onClick={() => scroll(index - selectedIndex)}
                                        role="button"
                                        aria-current={selected ? 'true' : null}
                                        aria-label={formatMessage(
                                            {
                                                id: 'global.carousel.slide_x_of_y',
                                                defaultMessage: 'Slide {index} of {total}'
                                            },
                                            {
                                                index: index + 1,
                                                total: items.length
                                            }
                                        )}
                                    >
                                        {isVideo ? (
                                            <Img
                                                {...styles.thumbnailImage}
                                                alt={item.alt}
                                                src={item.thumbnail || item.link}
                                            />
                                        ) : (
                                            <DynamicImage
                                                data-testid="dynamic-image-thumbnail"
                                                {...styles.thumbnailImage}
                                                src={`${
                                                    item.disBaseLink || item.link
                                                }[?sw={width}&q=60]`}
                                                widths={THUMBNAIL_WIDTHS}
                                                imageProps={{
                                                    ...styles.thumbnailImage?.imageProps,
                                                    alt: item.alt
                                                }}
                                            />
                                        )}
                                    </ListItem>
                                )
                            })}
                        </List>
                    )}

                    {showChildrenIn === 'container' && children}
                </Box>
            )}
        </>
    )
}

ImageGallery.propTypes = {
    /**
     * Allow autoplay for videos when specified in the video object
     */
    allowAutoplay: PropTypes.bool,
    /**
     * Aria label for the gallery
     */
    ariaLabel: PropTypes.string,
    /**
     * Children to render inside overlay
     */
    children: PropTypes.any,
    /**
     * The images/videos array to be rendered
     */
    items: PropTypes.array,
    /**
     * Additional props to pass to DynamicImage like `widths` and `imageProps`
     */
    imageProps: PropTypes.object,
    /**
     * List of hotspots per image name
     */
    hotspots: PropTypes.object,
    /**
     * Icon to use for left image navigation
     */
    navLeftIcon: PropTypes.any,
    /**
     * Icon to use for right image navigation
     */
    navRightIcon: PropTypes.any,
    /**
     * Index of the initially displayed image
     */
    index: PropTypes.number,
    /**
     * Should the first image be preloaded (useful for SSR)
     */
    preload: PropTypes.bool,
    /**
     * Specify where to render children
     */
    showChildrenIn: PropTypes.oneOf(['overlay', 'container']),
    /**
     * Should the pagination control be shown
     */
    showPagination: PropTypes.bool,
    /**
     * Should the thumbnails be shown
     */
    showThumbnails: PropTypes.bool,
    /**
     * The image gallery variant
     */
    variant: PropTypes.string,
    /**
     * Handler for click on image
     */
    onClick: PropTypes.func,
    /**
     * Handler for change of image
     */
    onChange: PropTypes.func,
    /**
     * Handler for click on hotspot
     */
    onHotspotClick: PropTypes.func
}

export default ImageGallery
